import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2, Plus } from "lucide-react";

import { listProjectContacts, type ProjectContact } from "@/api/development_suite";
import { ContactKindIcon, contactSummary, roleLabel } from "@/components/development_suite/contactDisplay";
import {
  developmentSuiteKeys,
  isDeniedError,
  retryDevelopmentSuiteRequest,
} from "@/components/development_suite/developmentSuiteQueries";
import { ListPager, RequestFailure } from "@/components/development_suite/DevelopmentSuiteStates";
import { ProjectContactDialog } from "@/components/development_suite/ProjectContactDialog";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/useToast";

type DialogState = { link: ProjectContact | null } | null;

/**
 * The people and organizations on one project and their roles there. Each
 * row is one role, so a contact holding two roles appears twice.
 */
export function ProjectContactsSection({ municipalityId, projectId }: { municipalityId: number; projectId: number }) {
  const headingId = "project-contacts-heading";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState>(null);

  const query = useQuery({
    queryKey: developmentSuiteKeys.projectContactsPage(municipalityId, projectId, page),
    queryFn: () => listProjectContacts(municipalityId, projectId, page),
    retry: retryDevelopmentSuiteRequest,
    placeholderData: (previous) => previous,
  });

  // A contact's details can change from here, so the picker's cache goes stale too.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: developmentSuiteKeys.contactDirectory(municipalityId) });
    return queryClient.invalidateQueries({ queryKey: developmentSuiteKeys.projectContacts(municipalityId, projectId) });
  };

  const handleSaved = (saved: ProjectContact, created: boolean) => {
    setDialog(null);
    if (created) setPage(1);
    void refresh();
    toast({ title: created ? `Added ${saved.contact.name}` : `Saved ${saved.contact.name}` });
  };

  // The list is refreshed before the dialog closes, so the row that opened it
  // is already gone and the dialog sends focus to the section heading instead.
  const handleRemoved = async (removed: ProjectContact) => {
    await refresh();
    setDialog(null);
    toast({ title: `Removed ${removed.contact.name} from this project` });
  };

  const links = query.isError ? [] : (query.data?.project_contacts ?? []);
  const pagination = query.isError ? undefined : query.data?.pagination;
  const roles = query.data?.roles ?? [];

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={headingId} tabIndex={-1} className="text-sm font-semibold outline-none">
          Contacts
          {pagination ? (
            <span className="ml-2 font-normal text-muted-foreground">
              <span className="sr-only">: </span>
              {pagination.total_count}
            </span>
          ) : null}
        </h3>
        {/* Adding needs the server's role list, so the button waits for a successful load. */}
        {query.data && !isDeniedError(query.error) ? (
          <Button size="sm" variant="outline" onClick={() => setDialog({ link: null })}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Add contact
          </Button>
        ) : null}
      </div>

      {query.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading contacts…
        </p>
      ) : query.isError ? (
        <RequestFailure error={query.error} what="Contacts" onRetry={() => void query.refetch()} />
      ) : links.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-5 text-center text-sm">
          <p className="font-medium">No contacts yet.</p>
          <p className="mt-1 text-muted-foreground">
            Add the owner, applicant, or designer so staff know who is involved.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <Table aria-labelledby={headingId} className="table-fixed">
            <colgroup>
              <col />
              <col className="w-28 sm:w-32" />
              <col className="hidden w-28 sm:table-column" />
              <col className="w-10" />
            </colgroup>
            <TableHeader className="bg-muted/40">
              <TableRow className="hover:bg-transparent">
                <TableHead scope="col" className="px-4">
                  Contact
                </TableHead>
                <TableHead scope="col" className="px-3">
                  Role
                </TableHead>
                <TableHead scope="col" className="hidden px-3 sm:table-cell">
                  Added
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Open contact</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((link) => (
                <TableRow
                  key={link.id}
                  className="group cursor-pointer focus-within:bg-muted/50"
                  onClick={() => setDialog({ link })}
                >
                  <TableCell className="px-4 py-3">
                    {/* The row handles the click; Enter on this button bubbles to it. A
                        contact with two roles appears twice, so the role is part of the name. */}
                    <button
                      type="button"
                      aria-label={`${link.contact.name}, ${roleLabel(link.role)}`}
                      className="flex w-full items-start gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ContactKindIcon
                        kind={link.contact.kind}
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                      />
                      <span className="min-w-0">
                        <span className="line-clamp-2 break-words font-medium">{link.contact.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {contactSummary(link.contact)}
                        </span>
                      </span>
                    </button>
                  </TableCell>
                  <TableCell className="px-3 py-3">
                    <Badge variant="secondary" className="font-normal">
                      {roleLabel(link.role)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden whitespace-nowrap px-3 py-3 text-xs text-muted-foreground sm:table-cell">
                    {formatRelativeTime(link.created_at)}
                  </TableCell>
                  <TableCell className="pr-4 text-muted-foreground group-focus-within:text-foreground group-hover:text-foreground">
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination ? <ListPager pagination={pagination} label="Contacts" onPageChange={setPage} /> : null}

      {dialog ? (
        <ProjectContactDialog
          municipalityId={municipalityId}
          projectId={projectId}
          roles={roles}
          link={dialog.link}
          fallbackFocusId={headingId}
          // A save that failed partway may have changed the contact already.
          onClose={() => {
            setDialog(null);
            void refresh();
          }}
          onSaved={handleSaved}
          onRemoved={handleRemoved}
        />
      ) : null}
    </section>
  );
}
